import { apiFetch } from '@app/lib/api'
import type { DashboardSummary } from '../../../worker/api/dashboard.types'

/**
 * Data layer for the dashboard screen.
 *
 * The response type is imported type-only from the Worker's API contract, so
 * there is a single type source shared between the API and the screen. Helpers
 * live here so `lib/api.ts` can stay a plain thin fetch wrapper shared by every
 * module.
 */

export type * from '../../../worker/api/dashboard.types'

export interface DashboardParams {
  /** Number of days the output chart covers (1-90). */
  days: number
  /** Length of the recent order / emir lists (1-50). */
  limit: number
}

export const DASHBOARD_DEFAULTS: DashboardParams = { days: 7, limit: 5 }

export function fetchDashboardSummary({ days, limit }: DashboardParams): Promise<DashboardSummary> {
  const query = new URLSearchParams({ days: String(days), limit: String(limit) })
  return apiFetch<DashboardSummary>(`/dashboard/summary?${query.toString()}`)
}

export const dashboardQueryKey = (params: DashboardParams) =>
  ['dashboard', 'summary', params.days, params.limit] as const

/* ── Formatting ─────────────────────────────────────────────────────────────── */

const numberFormat = new Intl.NumberFormat('tr-TR')

/** Pair count: whole number, thousands-separated (20.600). */
export function formatPairs(value: number): string {
  return numberFormat.format(Math.round(value))
}

/**
 * Korgun currency code → symbol (`P_DOV_Tip`: EU=EURO, TL=Turkish Lira,
 * US=Dollar). Deliberately a local copy of the map in `features/cari/format.ts`:
 * the panel must not depend on another feature's private helpers.
 */
const CURRENCY_SYMBOL: Record<string, string> = {
  TL: '₺',
  EU: '€',
  US: '$',
}

export function currencySymbol(code: string | null): string {
  if (!code) return ''
  return CURRENCY_SYMBOL[code] ?? code
}

const amountFormat = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/** Money with its symbol — "30.370.179,63 ₺". Same shape as the cari screens. */
export function formatAmount(value: number, code: string | null = null): string {
  const symbol = currencySymbol(code)
  return symbol ? `${amountFormat.format(value)} ${symbol}` : amountFormat.format(value)
}

const compactFormat = new Intl.NumberFormat('tr-TR', {
  notation: 'compact',
  maximumFractionDigits: 1,
})

/**
 * Money for the big tile number: 30.370.179,63 → "30,4 Mn" (Turkish CLDR
 * short form — B = bin, Mn = milyon). A receivable can run to eight digits and
 * the tile is a third of a row wide; the exact figure is printed underneath,
 * per currency, so nothing is lost.
 */
export function formatCompactAmount(value: number): string {
  return compactFormat.format(value)
}

const clockFormat = new Intl.DateTimeFormat('tr-TR', { hour: '2-digit', minute: '2-digit' })

/**
 * Epoch milliseconds → '14:32'.
 *
 * Deliberately the VIEWER's clock, not Istanbul's: this stamps "when did this
 * browser last get an answer", not a Korgun timestamp. (The two are the same
 * clock in the factory anyway.) It exists because the screen refetches itself
 * every 60 seconds — without it, a stale tab left open on a wall display looks
 * exactly like a live one.
 */
export function formatClock(epochMs: number): string {
  return clockFormat.format(new Date(epochMs))
}

/** 'YYYY-MM-DD' → '31.07.2026'. Does not go through Date, so no timezone drift. */
export function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const [year, month, day] = iso.split('-')
  if (!year || !month || !day) return iso
  return `${day}.${month}.${year}`
}

/** 'YYYY-MM-DD' → '31.07'. Short form for chart axes. */
export function shortDate(iso: string): string {
  const [, month, day] = iso.split('-')
  return month && day ? `${day}.${month}` : iso
}

const DAY_NAMES = ['Pz', 'Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct'] as const

/** 'YYYY-MM-DD' → 'Pe'. Also used to tell weekend columns apart. */
export function dayAbbreviation(iso: string): string {
  const day = new Date(`${iso}T12:00:00`).getDay()
  return DAY_NAMES[day] ?? ''
}

export function isWeekend(iso: string): boolean {
  const day = new Date(`${iso}T12:00:00`).getDay()
  return day === 0 || day === 6
}

/** Is it Monday? Used to mark weekly boundaries on long chart windows. */
export function isWeekStart(iso: string): boolean {
  return new Date(`${iso}T12:00:00`).getDay() === 1
}
